import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import { getCurrentUser, fetchUserAttributes } from "@aws-amplify/auth";
import usePublicChatStore from "./usePublicChatStore";
import AgentSessionsPanel from "./AgentSessionsPanel";
import AuthenticatedUserChip from "../common/AuthenticatedUserChip";
import CustomPaper from "../common/CustomPaper";
import { AuthGateScreen, EmailMagicLinkGateScreen } from "../access/PublicAccessGateScreens";

const ACCESS_MODE_EMAIL = "EMAIL_MAGIC_LINK";
const ACCESS_MODE_AUTHENTICATED_USERS = "AUTHENTICATED_USERS";
const ACCESS_MODE_AUTHENTICATED_SELECTED_USERS = "AUTHENTICATED_SELECTED_USERS";

const STORAGE_PREFIX_VERIFICATION_GRANT = "w86_public_chat_verification_grant_";
const STORAGE_PREFIX_VERIFIED_EMAIL = "w86_public_chat_verified_email_";
const STORAGE_ANON_ID = "w86_public_chat_anon_id";

const AUTH_REQUIRED_MODES = new Set([
  ACCESS_MODE_AUTHENTICATED_USERS,
  ACCESS_MODE_AUTHENTICATED_SELECTED_USERS,
]);

const parseErrorMessage = (error, fallback) => {
  const responseData = error?.response?.data;
  if (responseData && typeof responseData === "object") {
    return responseData.error || responseData.message || fallback;
  }
  if (typeof responseData === "string") {
    try {
      const parsed = JSON.parse(responseData);
      return parsed?.error || parsed?.message || fallback;
    } catch (_error) {
      return responseData || fallback;
    }
  }
  return fallback;
};

const getOrCreateAnonymousId = () => {
  const existing = sessionStorage.getItem(STORAGE_ANON_ID);
  if (existing) return existing;
  const next = crypto.randomUUID();
  sessionStorage.setItem(STORAGE_ANON_ID, next);
  return next;
};

export default function PublicAgentChatPage() {
  const { token } = useParams();
  const location = useLocation();
  const navigate = useNavigate();

  const [bootstrap, setBootstrap] = useState(null);
  const [bootstrapLoading, setBootstrapLoading] = useState(true);
  const [bootstrapError, setBootstrapError] = useState("");

  const [sessionError, setSessionError] = useState("");

  const [publicSessionToken, setPublicSessionToken] = useState(() => {
    const queryToken = new URLSearchParams(location.search).get("session");
    if (queryToken) return queryToken;
    return "";
  });
  const [verificationGrantToken, setVerificationGrantToken] = useState(
    sessionStorage.getItem(`${STORAGE_PREFIX_VERIFICATION_GRANT}${token}`) || ""
  );
  const [verifiedEmail, setVerifiedEmail] = useState(
    sessionStorage.getItem(`${STORAGE_PREFIX_VERIFIED_EMAIL}${token}`) || ""
  );
  const [newChatRequestNonce, setNewChatRequestNonce] = useState(0);
  const [cognitoEmail, setCognitoEmail] = useState("");

  const anonymousId = useMemo(() => getOrCreateAnonymousId(), []);
  const accessMode = String(bootstrap?.accessMode || "").toUpperCase();
  const authRequired = AUTH_REQUIRED_MODES.has(accessMode);
  const showSharedSessionsSidebar =
    bootstrap?.showSharedSessionsSidebar !== false;
  const needsEmailVerification =
    accessMode === ACCESS_MODE_EMAIL && !verificationGrantToken;

  const identityPayload = useMemo(
    () => ({
      accessMode,
      anonymousId,
      verificationGrantToken: verificationGrantToken || undefined,
      verifiedEmail: verifiedEmail || undefined,
      agentId: bootstrap?.agentId || undefined,
    }),
    [
      accessMode,
      anonymousId,
      verificationGrantToken,
      verifiedEmail,
      bootstrap?.agentId,
    ]
  );

  useEffect(() => {
    if (!authRequired) return;
    let cancelled = false;
    (async () => {
      try {
        await getCurrentUser();
        const attrs = await fetchUserAttributes();
        if (!cancelled) setCognitoEmail(attrs.email || attrs.preferred_username || "");
      } catch { /* not authenticated */ }
    })();
    return () => { cancelled = true; };
  }, [authRequired]);

  const publicUserDisplayName = verifiedEmail || cognitoEmail || "";

  const applyVerifiedGrant = (grantResponse) => {
    const data = grantResponse?.data || {};
    const nextGrantToken = data?.verificationGrantToken || "";
    const nextEmail = data?.email || "";
    setVerificationGrantToken(nextGrantToken);
    setVerifiedEmail(nextEmail);
    if (nextGrantToken) {
      sessionStorage.setItem(
        `${STORAGE_PREFIX_VERIFICATION_GRANT}${token}`,
        nextGrantToken
      );
    }
    if (nextEmail) {
      sessionStorage.setItem(`${STORAGE_PREFIX_VERIFIED_EMAIL}${token}`, nextEmail);
    }
  };

  const loadBootstrap = async () => {
    setBootstrapLoading(true);
    setBootstrapError("");
    try {
      const data = await usePublicChatStore.getState().bootstrapPublicChat(token);
      setBootstrap(data);
    } catch (error) {
      setBootstrapError(
        parseErrorMessage(error, "Unable to open this public chat link.")
      );
    } finally {
      setBootstrapLoading(false);
    }
  };

  const handlePublicSessionTokenChange = (nextSessionTokenRaw) => {
    const nextSessionToken = String(nextSessionTokenRaw || "");
    setPublicSessionToken(nextSessionToken);
  };

  useEffect(() => {
    loadBootstrap();
  }, [token]);

  useEffect(() => {
    const sessionTokenFromUrl =
      new URLSearchParams(location.search).get("session") || "";
    const nextSessionToken = sessionTokenFromUrl;
    if (nextSessionToken !== publicSessionToken) {
      setPublicSessionToken(nextSessionToken);
    }
    if (sessionTokenFromUrl) {
      const params = new URLSearchParams(location.search);
      params.delete("session");
      const nextSearch = params.toString();
      navigate(
        {
          pathname: location.pathname,
          search: nextSearch ? `?${nextSearch}` : "",
        },
        { replace: true }
      );
    }
  }, [location.pathname, location.search, navigate, publicSessionToken]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const verifyToken = params.get("verifyToken");
    if (!verifyToken) return;
    params.delete("verifyToken");
    const nextSearch = params.toString();
    navigate(
      {
        pathname: location.pathname,
        search: nextSearch ? `?${nextSearch}` : "",
      },
      { replace: true }
    );

    usePublicChatStore.getState()
      .verifyPublicChatMagicLink(verifyToken)
      .then((response) => {
        applyVerifiedGrant(response);
      })
      .catch((error) => {
        setSessionError(
          parseErrorMessage(error, "Magic-link verification failed.")
        );
      });
  }, [location.pathname, location.search, navigate, token]);

  const handleRequestNewChat = () => {
    setNewChatRequestNonce((current) => current + 1);
  };

  if (bootstrapLoading) {
    return (
      <Box
        sx={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <CircularProgress size={28} />
      </Box>
    );
  }

  if (bootstrapError) {
    return (
      <Box sx={{ minHeight: "100vh", p: 3, bgcolor: "action.hover" }}>
        <CustomPaper sx={{ maxWidth: 780, mx: "auto", p: 2 }}>
          <Alert severity="error">{bootstrapError}</Alert>
        </CustomPaper>
      </Box>
    );
  }

  if (authRequired && !bootstrap?.authenticated) {
    return <AuthGateScreen onSignedIn={loadBootstrap} />;
  }

  if (needsEmailVerification) {
    return (
      <EmailMagicLinkGateScreen
        onRequestMagicLink={async (email) => {
          await usePublicChatStore.getState().requestPublicChatMagicLink(token, email);
        }}
      />
    );
  }

  return (
    <Box
      sx={{
        height: "100dvh",
        minHeight: "100dvh",
        width: "100%",
        bgcolor: "background.paper",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Box sx={{ px: 2, pt: 2, pb: 1.5 }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "space-between",
            gap: 2,
          }}
        >
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography
              sx={{ fontSize: "20px", fontWeight: 700, color: "text.primary" }}
            >
              {bootstrap?.agentName || "Agent Chat"}
            </Typography>
            {bootstrap?.agentDescription && (
              <Typography sx={{ fontSize: "13px", color: "text.secondary", mt: 0.5 }}>
                {bootstrap.agentDescription}
              </Typography>
            )}
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flexShrink: 0 }}>
            <AuthenticatedUserChip />
            {!showSharedSessionsSidebar && (
              <Button
                variant="outlined"
                size="small"
                startIcon={<AddIcon sx={{ fontSize: 16 }} />}
                onClick={handleRequestNewChat}
                sx={{
                  textTransform: "none",
                  flexShrink: 0,
                  borderColor: "divider",
                  color: "text.primary",
                  "&:hover": {
                    borderColor: "divider",
                    backgroundColor: "action.hover",
                  },
                }}
              >
                New chat
              </Button>
            )}
          </Box>
        </Box>

        {sessionError && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {sessionError}
          </Alert>
        )}
      </Box>

      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          borderTop: 1, borderColor: "divider",
          bgcolor: "background.paper",
          overflow: "hidden",
        }}
      >
        <AgentSessionsPanel
          mode="public"
          publicShareToken={token}
          publicIdentityPayload={identityPayload}
          initialPublicSessionToken={
            showSharedSessionsSidebar
              ? publicSessionToken
              : new URLSearchParams(location.search).get("session") || ""
          }
          onPublicSessionTokenChange={handlePublicSessionTokenChange}
          agentId={bootstrap?.agentId || ""}
          agentName={bootstrap?.agentName || "Agent"}
          agentDescription={bootstrap?.agentDescription || ""}
          agentInboxAddress=""
          showSessionSidebar={showSharedSessionsSidebar}
          newChatRequestNonce={newChatRequestNonce}
          chatUiPolicy={{
            showSystemPromptInChat: false,
            showToolCallsAndOutputsInChat: true,
            compactToolCalls: true,
            showInterAgentMessagesInChat: false,
            showWorkflowProvenanceInChat: false,
            showSchedulingActivityInChat: false,
            showSessionOperationalMetaInChat: false,
            showSupervisorControlMessagesInChat: false,
            enableHumanTakeoverMode: false,
            forceUserNameAsYou: true,
            showSharedSessionsSidebar,
            showTaskQueuePlannerInChat: false,
          }}
          composerDisabled={false}
          hideWelcomeComposerInput={false}
          publicUserDisplayName={publicUserDisplayName}
        />
      </Box>
    </Box>
  );
}
